import type { Bank, BankService } from '@/common/settings.common'
import { API } from '.'
import type { PaginationResult } from './dto/misc.dto'
import type {
  ContractSettingsDTO,
  ContractSettingsUpdatePayload,
  GetOrderCapacityResponse,
  OrderCapacityYearsResponse,
  InvoiceSettingsData,
  NotificationSettingsResponseDTO,
  UpdateOrderSettingsDto,
  ServiceCatalogItem,
  BankServicesResponse,
  CreateOrUpdateBankPayload,
  BankDiscountDTO,
  UpsertBankDiscountPayload,
  CreateBankDiscountPayload,
  ContactBookDto,
  ContactBookListResponse,
  CreateContactBookRequest,
  UpdateContactBookRequest,
  CapacityChangeListResponse,
  BankServicePriceChangeListResponse,
  UpsertBankServicePayload,
} from './dto/settings.dto'
import { type UserProfile } from '@/common/user.common'
import type { UnionBank } from '@/common/unionBank.common'

/* --------------------------------------------- */
/*      CONTRACT SETTINGS API CALLS              */
/* --------------------------------------------- */
export function getContractSettings() {
  return API<ContractSettingsDTO>(
    '/settings/subscription',
    {},
    'Fehler beim Laden der Vertragsdaten',
    'CONTRACT_SETTINGS_FETCH_ERROR'
  )
}

export function updateContractSettings(data: ContractSettingsUpdatePayload) {
  return API<ContractSettingsDTO>(
    '/settings/subscription',
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
    'Fehler beim Aktualisieren der Vertragsdaten',
    'CONTRACT_SETTINGS_UPDATE_ERROR'
  )
}

export function cancelContract() {
  return API<ContractSettingsDTO>(
    '/settings/subscription/cancel',
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
    'Fehler beim Kündigen des Vertrags',
    'CONTRACT_CANCEL_ERROR'
  )
}

export function reactivateContract() {
  return API<ContractSettingsDTO>(
    '/settings/subscription/reactivate',
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
    'Fehler beim Reaktivieren des Vertrags',
    'CONTRACT_REACTIVATE_ERROR'
  )
}

/* --------------------------------------------- */
/*      INVOICE SETTINGS API CALLS               */
/* --------------------------------------------- */
export function getInvoiceSettings() {
  return API<InvoiceSettingsData>(
    '/settings/invoice',
    {},
    'Fehler beim Laden der Einstellungen',
    'INVOICE_SETTINGS_FETCH_ERROR'
  )
}

export function updateInvoiceSettings(
  data: Partial<InvoiceSettingsData>
): Promise<InvoiceSettingsData> {
  return API<InvoiceSettingsData>(
    '/settings/invoice',
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
    'Fehler beim Speichern der Einstellungen',
    'INVOICE_SETTINGS_UPDATE_ERROR'
  )
}

/* --------------------------------------------- */
/*      PROFILE SETTINGS API CALLS               */
/* --------------------------------------------- */
export function getProfileSettings() {
  return API<UserProfile>(
    `/settings/profile`,
    {},
    'Fehler beim Laden der Profileinstellungen',
    'PROFILE_SETTINGS_FETCH_ERROR'
  )
}

export function updateProfileSettings(changes: any) {
  return API(
    `/settings/profile`,
    {
      method: 'PUT',
      body: JSON.stringify(changes),
    },
    'Fehler beim Aktualisieren der Profileinstellungen',
    'PROFILE_SETTINGS_UPDATE_ERROR'
  )
}

export function changeEmail(newEmail: string) {
  return API(
    `/settings/profile/email`,
    {
      method: 'PUT',
      body: JSON.stringify({ newEmail }),
    },
    'Fehler beim Ändern der E-Mail-Adresse',
    'PROFILE_EMAIL_CHANGE_ERROR'
  )
}

export function changePassword(currentPassword: string, newPassword: string) {
  return API(
    `/settings/profile/password`,
    {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    },
    'Fehler beim Ändern des Kennworts',
    'PROFILE_PASSWORD_CHANGE_ERROR'
  )
}

/* --------------------------------------------- */
/*      BANK SETTINGS API CALLS                  */
/* --------------------------------------------- */

const bankPromiseCache = new Map<number, Promise<Bank>>()

export function getBankByIdCached(id: number) {
  if (!bankPromiseCache.has(id)) {
    bankPromiseCache.set(id, getBankById(id))
  }
  return bankPromiseCache.get(id)!
}

export function getBanks(
  params: {
    parentId?: number
    page?: number
    pageSize?: number
    includeInactive?: boolean
    search?: string
    status?: 'ACTIVE' | 'INACTIVE' | 'ALL'
    sortBy?: 'name' | 'companyCode'
    sortDir?: 'asc' | 'desc'
  } = {}
) {
  const query = new URLSearchParams()
  query.set('parentId', String(params.parentId ?? 0))

  if (params.page !== undefined) query.set('page', String(params.page))
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize))
  if (params.includeInactive !== undefined)
    query.set('includeInactive', String(params.includeInactive))
  if (params.search) query.set('search', params.search)
  if (params.status && params.status !== 'ALL') query.set('status', params.status)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortDir) query.set('sortDir', params.sortDir)

  return API<PaginationResult<Bank>>(
    `/settings/banks?${query.toString()}`,
    {},
    'Fehler beim Laden der Banken',
    'BANKS_FETCH_ERROR'
  )
}

export async function getAllBanks(
  params: Omit<NonNullable<Parameters<typeof getBanks>[0]>, 'page' | 'pageSize'> & {
    pageSize?: number
  } = {}
): Promise<Bank[]> {
  const pageSize = params.pageSize ?? 100

  let page = 1
  const all: Bank[] = []

  // hard guard against buggy pagination backend
  const MAX_PAGES = 500

  for (let i = 0; i < MAX_PAGES; i++) {
    const res = await getBanks({ ...params, page, pageSize })
    const items = res.items ?? []

    if (items.length === 0) break

    all.push(...items)

    if (items.length < pageSize) break

    if (typeof res.totalItems === 'number') {
      const totalPages = Math.ceil(res.totalItems / pageSize)
      if (page >= totalPages) break
    }

    if (typeof (res as any).totalPages === 'number') {
      const totalPages = (res as any).totalPages
      if (page >= totalPages) break
    }

    page += 1
  }

  return all
}

/**
 * Simple in-memory cache for bankId → bankName mapping.
 * Used mainly on dashboards (e.g. Union manager view).
 */
let bankNameCache: Record<number, string> | null = null

async function loadBankNameCache(): Promise<Record<number, string>> {
  if (bankNameCache) return bankNameCache

  const res = await getBanks()
  const map: Record<number, string> = {}

  res.items.forEach(b => {
    map[b.id] = b.name
  })

  bankNameCache = map
  return map
}

/**
 * Returns a lookup object bankId → bankName.
 * Call this once in a React effect and keep the map in state.
 */
export function getBankNameLookup(): Promise<Record<number, string>> {
  return loadBankNameCache()
}

export function createBank(payload: CreateOrUpdateBankPayload) {
  return API(
    `/settings/banks`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Fehler beim Erstellen der Bank',
    'BANK_CREATE_ERROR'
  )
}

export function updateBank(id: number, payload: CreateOrUpdateBankPayload) {
  return API(
    `/settings/banks/${id}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    'Fehler beim Aktualisieren der Bank',
    'BANK_UPDATE_ERROR'
  )
}

export function deleteBank(id: number) {
  return API(
    `/settings/banks/${id}`,
    {
      method: 'DELETE',
    },
    'Fehler beim Löschen der Bank',
    'BANK_DELETE_ERROR'
  )
}

/* --------------------------------------------- */
/*      CONTACT BOOK API CALLS                  */
/* --------------------------------------------- */

export function getContactBooks(params: {
  userId?: string
  bankId?: number
  customerId?: number
  sort?: string[]
  page?: number
  pageSize?: number
}) {
  const query = new URLSearchParams()

  if (params.userId) query.set('userId', params.userId)
  if (params.bankId !== undefined) query.set('bankId', String(params.bankId))
  if (params.customerId !== undefined) query.set('customerId', String(params.customerId))
  if (params.page !== undefined) query.set('page', String(params.page))
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize))
  for (const sort of params.sort ?? []) query.append('sort', sort)

  return API<ContactBookListResponse>(
    `/contact-books?${query.toString()}`,
    {},
    'Fehler beim Laden des Kontaktbuchs',
    'CONTACT_BOOK_FETCH_ERROR'
  )
}

export function createContactBook(payload: CreateContactBookRequest) {
  return API<ContactBookDto>(
    `/contact-books`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Fehler beim Erstellen des Beraters',
    'CONTACT_BOOK_CREATE_ERROR'
  )
}

export function updateContactBook(id: number, payload: UpdateContactBookRequest) {
  return API<ContactBookDto>(
    `/contact-books/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Fehler beim Aktualisieren des Beraters',
    'CONTACT_BOOK_UPDATE_ERROR'
  )
}

export function deleteContactBook(id: number) {
  return API(
    `/contact-books/${id}`,
    {
      method: 'DELETE',
    },
    'Fehler beim Löschen des Beraters',
    'CONTACT_BOOK_DELETE_ERROR'
  )
}

/* --------------------------------------------- */
/*      NOTIFICATION SETTINGS API CALLS         */
/* --------------------------------------------- */
export function getNotificationSettings() {
  return API<NotificationSettingsResponseDTO>(
    '/settings/profile',
    {},
    'Fehler beim Laden der Benachrichtigungseinstellungen',
    'NOTIFICATION_SETTINGS_FETCH_ERROR'
  )
}

export function updateNotificationSettings(payload: any) {
  return API(
    `/settings/profile`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Fehler beim Aktualisieren der Benachrichtigungseinstellungen',
    'NOTIFICATION_SETTINGS_UPDATE_ERROR'
  )
}

/* --------------------------------------------- */
/*      ORDER CAPACITIES SETTINGS API CALLS     */
/* --------------------------------------------- */
export function getOrderCapacities(): Promise<GetOrderCapacityResponse> {
  return API<GetOrderCapacityResponse>(
    `/settings/orders`,
    {},
    'Fehler beim Laden der Auftragskapazitäten',
    'ORDER_CAPACITIES_FETCH_ERROR'
  )
}

export async function updateOrderCapacities(payload: UpdateOrderSettingsDto) {
  return API(
    `/settings/orders`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Fehler beim Aktualisieren der Auftragskapazitäten',
    'ORDER_CAPACITIES_UPDATE_ERROR'
  )
}

/**
 * The authenticated SIB's capacities for every planning year, each value marked as saved for that
 * year, carried over from the previous one, or missing (SIB-928). Not cached, so a year that
 * opened since the last read shows up.
 */
export function getOrderCapacityYears() {
  return API<OrderCapacityYearsResponse>(
    `/settings/orders/years`,
    { headers: { 'x-disable-cache': '1' } },
    'Fehler beim Laden der Kapazitäten je Jahr',
    'ORDER_CAPACITY_YEARS_FETCH_ERROR'
  )
}

/* --------------------------------------------- */
/*      CAPACITY VERSION HISTORY (SIB-939)       */
/* --------------------------------------------- */

/**
 * The authenticated user's own capacity version history, newest first, including planned and
 * dropped versions.
 */
export function getCapacityChangeHistory() {
  return API<CapacityChangeListResponse>(
    `/settings/orders/capacity-changes`,
    { headers: { 'x-disable-cache': '1' } },
    'Fehler beim Laden des Kapazitätsverlaufs',
    'CAPACITY_HISTORY_FETCH_ERROR'
  )
}

/**
 * Drops a planned capacity change. The row is kept and marked as dropped; the value in effect
 * simply continues. A version whose date has arrived cannot be dropped.
 */
export function dropPlannedCapacityChange(changeId: number) {
  return API<void>(
    `/settings/orders/capacity-changes/${changeId}`,
    { method: 'DELETE' },
    'Fehler beim Verwerfen der geplanten Änderung',
    'CAPACITY_CHANGE_DROP_ERROR'
  )
}

export async function getUserOrderCapacity(userId: string) {
  return API<GetOrderCapacityResponse>(
    `/settings/orders/${userId}`,
    {},
    'Fehler beim Laden der Auftragskapazität des Benutzers',
    'USER_ORDER_CAPACITY_FETCH_ERROR'
  )
}

export function getBankById(id: number) {
  return API<Bank>(`/settings/banks/${id}`, {}, 'Fehler beim Laden der Bank', 'BANK_FETCH_ERROR')
}

/**
 * Creates or updates one bank service price. A change to an existing, versioned price must name
 * its `validFrom`; the live price only moves once that date arrives (SIB-939).
 */
export function updateBankService(bankId: number, payload: UpsertBankServicePayload) {
  return API<BankService>(
    `/settings/banks/${bankId}/bankServices`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Fehler beim Aktualisieren der Leistung',
    'BANK_SERVICE_UPDATE_ERROR'
  )
}

/**
 * The authenticated user's price version history with one bank, newest first, including planned
 * and dropped versions (SIB-939).
 */
export function getPriceChangeHistory(bankId: number) {
  return API<BankServicePriceChangeListResponse>(
    `/settings/banks/${bankId}/bankServices/price-changes`,
    { headers: { 'x-disable-cache': '1' } },
    'Fehler beim Laden des Preisverlaufs',
    'PRICE_HISTORY_FETCH_ERROR'
  )
}

/**
 * Drops a planned price change. The row is kept and marked as dropped; the price in effect simply
 * continues. A version whose date has arrived cannot be dropped.
 */
export function dropPlannedPriceChange(bankId: number, changeId: number) {
  return API<void>(
    `/settings/banks/${bankId}/bankServices/price-changes/${changeId}`,
    { method: 'DELETE' },
    'Fehler beim Verwerfen der geplanten Änderung',
    'PRICE_CHANGE_DROP_ERROR'
  )
}

/**
 * Delete a specific bank service
 */
export function deleteBankService(bankId: number, serviceId: number) {
  return API(
    `/settings/banks/${bankId}/bankServices/${serviceId}`,
    {
      method: 'DELETE',
    },
    'Fehler beim Löschen der Leistung',
    'BANK_SERVICE_DELETE_ERROR'
  )
}

/**
 * Get bank services
 */
export function getServices() {
  return API<ServiceCatalogItem[]>(
    `/services`,
    {},
    'Fehler beim Laden der Leistungen',
    'SERVICES_FETCH_ERROR'
  )
}

/**
 * Create new bank service
 */

export function createService(payload: { code: string; name: string }) {
  return API<ServiceCatalogItem>(
    `/services`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Fehler beim Erstellen der Leistung',
    'SERVICES_CREATE_ERROR'
  )
}

export function getBankServices(bankId: number, page = 1, pageSize = 50) {
  const qs = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  }).toString()

  return API<BankServicesResponse>(
    `/settings/banks/${bankId}/bankServices?${qs}`,
    { method: 'GET' },
    'Fehler beim Laden der Bank-Leistungen',
    'BANK_SERVICES_FETCH_ERROR'
  )
}

/**
 * Get a specific SIB's bank services (unit prices) for a given bank.
 * Union managers use this to see each SIB's agreed prices; the Union bank is id 1.
 */
export function getSibBankServices(userId: string, bankId = 1) {
  return API<BankServicesResponse>(
    `/admin/users/${encodeURIComponent(userId)}/banks/${bankId}/bankServices`,
    { method: 'GET' },
    'Fehler beim Laden der SIB-Preise',
    'SIB_BANK_SERVICES_FETCH_ERROR'
  )
}

// Union details (full payload from GET /settings/banks/{id})
export function getUnionBankById(id: number) {
  return API<UnionBank>(
    `/settings/banks/${id}`,
    {},
    'Fehler beim Laden der Bank',
    'BANK_FETCH_ERROR'
  )
}
//Set Active-Inactive Status of Bank
export function patchBankStatus(bankId: number, active: boolean) {
  const qs = new URLSearchParams({ active: String(active) }).toString()
  return API(
    `/settings/banks/${bankId}/status?${qs}`,
    { method: 'PATCH' },
    'Fehler beim Aktualisieren des Status',
    'BANK_STATUS_UPDATE_ERROR'
  )
}

//Bank Discounts

export function getBankDiscount(bankId: number) {
  return API<BankDiscountDTO[] | BankDiscountDTO>(
    `/discounts/bank/${bankId}`,
    { method: 'GET' },
    'Fehler beim Laden des Rabatts',
    'BANK_DISCOUNT_FETCH_ERROR'
  )
}

export async function getBankDiscountOrNull(bankId: number): Promise<BankDiscountDTO | null> {
  try {
    const res = await getBankDiscount(bankId)
    // backend may return object or array – normalize here if you want
    const d = Array.isArray(res) ? (res[0] ?? null) : (res as any)
    return d ?? null
  } catch (e: any) {
    // adapt this to your API error shape:
    const status = e?.status ?? e?.response?.status ?? e?.httpStatus
    if (status === 404) return null
    throw e
  }
}

export function createBankDiscount(payload: CreateBankDiscountPayload) {
  return API<BankDiscountDTO>(
    `/discounts`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Fehler beim Speichern des Rabatts',
    'BANK_DISCOUNT_CREATE_ERROR'
  )
}

export function upsertBankDiscount(bankId: number, payload: UpsertBankDiscountPayload) {
  return API<BankDiscountDTO>(
    `/discounts/bank/${bankId}`,
    { method: 'PUT', body: JSON.stringify(payload) },
    'Fehler beim Speichern des Rabatts',
    'BANK_DISCOUNT_UPDATE_ERROR'
  )
}

export function deleteBankDiscount(bankId: number) {
  return API(
    `/discounts/bank/${bankId}`,
    { method: 'DELETE' },
    'Fehler beim Löschen des Rabatts',
    'BANK_DISCOUNT_DELETE_ERROR'
  )
}
